/**
 * When the embodied-carbon figure has to be reset, and when it has to be recalculated.
 *
 * Split out of syncMiddleware into a module with no imports so the decision can be exercised on its
 * own: it answers for ~20 action types across three materials, distinguishes an edit from a project
 * load, and has to catch a material switch that arrives disguised as a load. That is a decision
 * table, and a decision table is worth being able to test without a store.
 *
 * The middleware supplies the facts (which action, what the project looked like before and after,
 * whether there is a figure) and carries out the answer; everything about *why* lives here.
 */

/** What the readout is calculated from, as far as this decision is concerned. */
export interface CarbonProjectFacts {
  /** Identifies the project, so a material switch can be told apart from a different project. */
  guid?: string;
  /** "concrete" | "steel" | "timber", or null when there is no beam or none we recognise. */
  material: string | null;
}

export interface CarbonWork {
  /** Drop the figure now: what is on screen was calculated for input that no longer applies. */
  reset: boolean;
  /** Schedule a recalculation. False when the material has no endpoint for it. */
  update: boolean;
}

// Actions that change what the figure is calculated from. ALLOWLISTS, unlike the denylists in
// syncMiddleware, and deliberately so: pruning and saving are bookkeeping that must not miss
// anything, whereas this fires a calculation request, so an action belongs here only if it actually
// moves the number — quantity × emission factor, and nothing else.

/**
 * Triggers that hold for every material. The section and the material decide the volume and the
 * factor it is multiplied by; the column length scales that volume (and, for concrete, the bar
 * lengths with it), so it counts for steel and timber exactly as much as for concrete.
 */
export const CARBON_TRIGGERS_ALL_MATERIALS = new Set<string>([
  "ProjectSlice/setProjectSection",
  "ProjectSlice/setProjectMaterial",
  "ProjectSlice/setColumnLength",
  // An approved design result, which lands a new section (and for concrete new reinforcement with
  // it) for every material — so it belongs here rather than with the concrete-only triggers.
  "ProjectSlice/applyRecommendedDesign",
  // Opening a project — both routes into state.project, the thunk one (App) and the direct one
  // (temp files, material settings).
  "ProjectSlice/setProject",
  "projects/fetchProject/fulfilled",
]);

/**
 * Triggers that only exist for concrete: reinforcement is a material a steel or timber column does
 * not have. Reinforcement *detailing* that leaves the quantities alone (concrete cover, bar
 * positions) is deliberately absent — it does not move the number.
 */
export const CARBON_TRIGGERS_CONCRETE_ONLY = new Set<string>([
  // Longitudinal bars, stirrups, and their materials
  "ProjectSlice/updateLongitudinalBarsCount",
  "ProjectSlice/updateLongitudinalDiameter",
  "ProjectSlice/updateLongitudinalMaterialGuid",
  "ProjectSlice/updateRadialBarsCount",
  "ProjectSlice/updateRadialDiameter",
  "ProjectSlice/updateStirrupDiameter",
  "ProjectSlice/updateStirrupSpacing",
  "ProjectSlice/updateStirrupMaterialGuid",
  // Individually placed bars
  "ProjectSlice/addStandaloneBar",
  "ProjectSlice/updateStandaloneBar",
  "ProjectSlice/deleteStandaloneBar",
  "ProjectSlice/clearStandaloneBars",
  "ProjectSlice/regenerateStandaloneBarsFromCurrentRebars",
  "ProjectSlice/setExactBarCoordinates",
  // Reinforcement replaced wholesale by a chosen design suggestion
  "ProjectSlice/setElementReinforcement",
]);

/**
 * The actions above that open a project rather than edit one. A load keeps the figure the project
 * was stored with (it belongs to that project) and only calculates when it brought none.
 */
export const CARBON_LOAD_ACTION_TYPES = new Set<string>([
  "ProjectSlice/setProject",
  "projects/fetchProject/fulfilled",
]);

/**
 * Materials whose design service has the /carbonfootprint endpoint: RC-Design for concrete,
 * FEM-Design for steel and timber (same endpoint name in both — see carbonPathForMaterial in
 * server/src/routes/projects.ts, which maps the material to the service).
 *
 * All three the app supports are here, so the set no longer excludes anything a column can be built
 * from. It is kept rather than dropped because it states the one thing the middleware cannot derive:
 * whether a material has the endpoint at all. A material we do not recognise (carbonMaterialOf
 * answers null) still resets its figure like any other and waits for a code check to produce a new
 * one, instead of asking for a calculation no service performs.
 */
export const CARBON_LIVE_UPDATE_MATERIALS = new Set<string>(["concrete", "steel", "timber"]);

/** Which of the three the column is built from, read out of the project JSON. */
export const carbonMaterialOf = (project: any): string | null => {
  const beam = project?.data?.data?.input?.structures?.[0]?.elements?.[0]?.beam;
  return beam?.concrete ? "concrete" : beam?.steel ? "steel" : beam?.timber ? "timber" : null;
};

/**
 * Did the open project just change material?
 *
 * Switching a column's material replaces the project wholesale through setProject (see
 * SXInputSectionAndMaterial), which is otherwise how a project is *loaded* — and a load must keep the
 * figure it was stored with. Comparing the guid tells the two apart: the material switch keeps
 * working on the same project, a load brings a different one. Without this the figure calculated for
 * a concrete column stayed on screen next to the steel one that replaced it.
 */
export const isMaterialSwitch = (before: CarbonProjectFacts, after: CarbonProjectFacts): boolean =>
  before.material !== after.material && before.guid != null && before.guid === after.guid;

/**
 * The decision. `hasCarbon` must mean "is there a figure", not "is the array non-empty" — an entry
 * with an empty footprint is what a run with no emission data looks like, and it must not count as a
 * result (see projectHasCarbonFigure).
 */
export const decideCarbonWork = (args: {
  actionType: string;
  before: CarbonProjectFacts;
  after: CarbonProjectFacts;
  hasCarbon: boolean;
}): CarbonWork => {
  const { actionType, before, after, hasCarbon } = args;

  const switched = isMaterialSwitch(before, after);
  const isTrigger = CARBON_TRIGGERS_ALL_MATERIALS.has(actionType) || CARBON_TRIGGERS_CONCRETE_ONLY.has(actionType);

  // Anything else leaves the figure alone: a load change or a fire setting invalidates every other
  // result, but not one calculated from materials and geometry.
  if (!isTrigger && !switched) {
    return { reset: false, update: false };
  }

  // Opening a project is not an edit — unless it also switched the material, which is how the
  // material switch reaches us.
  const isEdit = !CARBON_LOAD_ACTION_TYPES.has(actionType) || switched;

  const supported =
    after.material !== null &&
    CARBON_LIVE_UPDATE_MATERIALS.has(after.material) &&
    // Reinforcement triggers belong to concrete columns only. A steel or timber column cannot
    // dispatch them today, but the check keeps that a fact about the data rather than a coincidence
    // of which panels are mounted.
    (after.material === "concrete" || !CARBON_TRIGGERS_CONCRETE_ONLY.has(actionType));

  return {
    // Reset first, always — and independently of whether a recalculation can follow. For a material
    // without a live endpoint this is the whole story: it resets and stays empty until the next code
    // check, instead of showing the previous material's number.
    reset: isEdit && hasCarbon,
    update: supported && (isEdit || !hasCarbon),
  };
};
